import type { PlayerState } from "../session.js";
import type { MasteryState } from "../mastery/masteryState.js";
import type { Question } from "../types.js";
import type { AttemptRecord, EnginePersistence } from "./types.js";

const DEF_PREFIX = "notanyai:";

/**
 * Browser `localStorage` implementation of `EnginePersistence` (sync API).
 *
 * No-op in environments without `localStorage` (e.g. some SSR); safe to construct.
 *
 * **All reads/writes are wrapped in try/catch.** A `setItem` can throw
 * `QuotaExceededError` (mobile quotas are small and easily hit on devices that
 * also store other PWA data) and any `getItem` can return malformed JSON if a
 * previous write was truncated. Either failure used to bubble out of these
 * methods, hit the module-top-level state init in `app/src/main.ts`, and blank
 * the page on every refresh with no recovery path. We now log + drop instead
 * so the synchronous boot path can always reach `render()`.
 */
export class LocalStoragePersistence implements EnginePersistence {
  constructor(private readonly keyPrefix: string = DEF_PREFIX) {}

  private get storage(): Storage | null {
    try {
      if (typeof globalThis === "object" && "localStorage" in globalThis) {
        return (globalThis as unknown as { localStorage: Storage }).localStorage;
      }
    } catch {
      // Some browsers throw just on accessing `localStorage` (e.g. iOS Safari
      // private mode, third-party-cookie blocking). Treat as "no storage".
    }
    return null;
  }

  private k(name: string): string {
    return `${this.keyPrefix}${name}`;
  }

  private safeGet(name: string): string | null {
    try {
      return this.storage?.getItem(this.k(name)) ?? null;
    } catch (err) {
      warnStorage("getItem", name, err);
      return null;
    }
  }

  private safeSet(name: string, value: string): void {
    try {
      this.storage?.setItem(this.k(name), value);
    } catch (err) {
      warnStorage("setItem", name, err);
    }
  }

  private safeParse<T>(raw: string | null): T | null {
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch (err) {
      warnStorage("parse", "<value>", err);
      return null;
    }
  }

  loadPlayer(): PlayerState | null {
    return this.safeParse<PlayerState>(this.safeGet("player"));
  }

  savePlayer(state: PlayerState): void {
    this.safeSet("player", JSON.stringify(state));
  }

  loadQuestionBank(): Question[] | null {
    return this.safeParse<Question[]>(this.safeGet("questions"));
  }

  saveQuestionBank(questions: Question[]): void {
    this.safeSet("questions", JSON.stringify(questions));
  }

  appendAttempt(record: AttemptRecord): void {
    const cur = (this.loadAttempts() ?? []) as AttemptRecord[];
    cur.push({ ...record });
    this.safeSet("attempts", JSON.stringify(cur));
  }

  loadAttempts(limit?: number): AttemptRecord[] {
    const a = this.safeParse<AttemptRecord[]>(this.safeGet("attempts"));
    if (!a) return [];
    if (limit === undefined) return a.map((r) => ({ ...r }));
    return a.slice(-limit).map((r) => ({ ...r }));
  }

  loadMastery(): MasteryState | null {
    const sNew = this.safeParse<MasteryState>(this.safeGet("mastery"));
    if (sNew) return sNew;
    return this.safeParse<MasteryState>(this.safeGet("browser-mastery"));
  }

  saveMastery(state: MasteryState): void {
    this.safeSet("mastery", JSON.stringify(state));
  }
}

function warnStorage(op: string, name: string, err: unknown): void {
  if (typeof console === "undefined") return;
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  console.warn(`[notanyai] localStorage ${op}(${name}) failed: ${msg}`);
}
